import { Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import { isEmpty, startCase } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  searchAdvertisers,
  searchCurrencies,
  searchBrandsWithAdvertiserId,
  categorySearchWithBrandAndText,
} from "../../../../../api";
import {
  CAMPAIGN_ACTIONS,
  CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST,
  FORM_FIELD_TYPE,
} from "../../../../../constants";
import styles from "../../../../../styles/CampaignCreate/CampaignForm.module.scss";
import { CustomMenuItem } from "../../../../common";
import Form from "../../../../common/Form";
import InfoBlock from "../../../../common/InfoBlock";
import Popup from "../../../../common/Popup";
import RemoveIcon from "../../../../common/RemoveIcon";
import { SearchIcon } from "../../../../common/SearchIcon";
import { getDateRange } from "../../utils/campaignCreate";
import EditAdvertiser from "../../../companies/EditAdvertiser";

export default class BasicDetails extends Component {
  state = {
    isFetchingPreferences: false,
    shouldSetCountryData: null,
    showAddClientPanel: false,
    prefillClientName: "",
    productCategories: [],
  };

  static propTypes = {
    campaign: PropTypes.object.isRequired,
    hasFormSaved: PropTypes.bool,
    createOrUpdateAdvertiser: PropTypes.func,
  };

  static defaultProps = {
    hasFormSaved: false,
  };

  handleClientCreate = async (advertiser) => {
    const { createOrUpdateAdvertiser } = this.props;
    if (!createOrUpdateAdvertiser) return;
    const saved = await createOrUpdateAdvertiser({}, [], advertiser);
    const savedAdvertiser = saved?.data || saved;

    const createdAdvertiser = {
      id: savedAdvertiser?.id,
      name: savedAdvertiser?.name || advertiser.name,
      displayName: savedAdvertiser?.name || advertiser.name,
      tag: savedAdvertiser?.cityName || advertiser.cityName,
    };

    this.setState({
      showAddClientPanel: false,
      prefillClientName: "",
    });

    if (createdAdvertiser.id) {
      this.onAdvertiserSelect(createdAdvertiser);
    }
  };
  // Separate method since we want to set state after preferences are fetched
  onAdvertiserSelect = async (advertiser) => {
    const {
      onInputChange,
      companyPreferences,
      companyId,
      getCompanyAdvertiserPreferences,
    } = this.props;
    onInputChange(advertiser, "advertiserId");
    this.setState({ isFetchingPreferences: true });

    //SLATE-1399 Nov21 Anushree:- action getCompanyAdvertiserPreferences called which takes two arguments companyId and advertiserId
    const { data: companyAdvertiserPreferences } = getCompanyAdvertiserPreferences(
      companyId,
      advertiser.id
    );
    const currency =
      companyAdvertiserPreferences?.campaignDefaultCurrency ||
      companyPreferences.campaignDefaultCurrency;
    const billingCycle =
      companyAdvertiserPreferences?.campaignDefaultBillingCycle ||
      companyPreferences.campaignDefaultBillingCycle;
    this.setState({ isFetchingPreferences: false });
    if (billingCycle) onInputChange(billingCycle, "billingCycle");
    //SLATE-1418 Dec6 Anushree:- currency is stored as currencyId in state
    //if (currency) onInputChange(currency, "currency");
    if (currency?.id) onInputChange(currency, "currencyId");
  };

  // Separate method since we want to confirm before changing currency
  onCurrencySelect = (data, isConfirmed = false) => {
    const { onInputChange, campaign } = this.props;
    if (isConfirmed || isEmpty(campaign?.currencyId) || campaign.currencyId === data.id) {
      onInputChange(data, "currencyId");
      if (isConfirmed) this.setState({ shouldSetCountryData: null });
    } else this.setState({ shouldSetCountryData: data });
  };
  onCountryRemove = (data, isConfirmed = false) => {
    const { campaign, onInputChange, removeCountryItem } = this.props;
    if (isConfirmed || isEmpty(campaign?.countryIds) || !campaign.id) {
      if (data === "clear") onInputChange(null, "countryIds");
      else removeCountryItem(data);
      if (isConfirmed) this.setState({ shouldSetCountryData: null });
    } else this.setState({ shouldSetCountryData: data });
  };
  // onCampaignTypeSelect = (data, isConfirmed = false) => {
  //   const { onInputChange, campaign } = this.props;
  //   if (isConfirmed || isEmpty(campaign?.campaignType) || campaign.campaignType.id === data.id)
  //     onInputChange(data, "campaignType");
  // };

  isSelectedCategory = (data) => {
    const {
      campaign: { categories },
    } = this.props;
    return categories?.find(
      (category) =>
        category.categoryId === data.categoryId &&
        category.subCategory1Id === data.subCategory1Id &&
        category.subCategory2Id === data.subCategory2Id
    );
  };

  arrowWithName = (n1, n2, n3) => {
    return [n1, n2, n3].filter((n) => n !== undefined && n !== null && n.length > 0).join(" > ");
  };

  categoryMenuRenderer = (data) => {
    return this.arrowWithName(data.categoryName, data.subCategory1Name, data.subCategory2Name);
  };

  renderBrandMenuName = (item) => {
    switch (item.type) {
      case "parentBrand":
        return `${item.parentBrandName}`;
      case "individualBrand":
        return `${item.parentBrandName}  ->  ${item.individualBrandName}`;
      case "product":
        return `${item.parentBrandName}  ->  ${item.individualBrandName}  ->  ${item.productName}`;
      case "variant":
        return `${item.parentBrandName}  ->  ${item.individualBrandName}  ->  ${item.productName}  ->  ${item.variantName}`;
      default:
        return `${item.parentBrandName}`;
    }
  };

  renderCategoryMenuItem = (data, { modifiers, handleClick }) => {
    return (
      <CustomMenuItem
        key={`${data.categoryId} ${data.subCategory1Id} ${data.subCategory2Id}`}
        active={modifiers?.active}
        icon={this.isSelectedCategory(data) ? IconNames.TICK : IconNames.BLANK}
        onClick={handleClick}
        text={this.categoryMenuRenderer(data)}
        shouldDismissPopover={false}
      />
    );
  };

  onBrandQueryChange = async (text) => {
    const { campaign } = this.props;

    if (campaign?.brandId) {
      const categories = await categorySearchWithBrandAndText(campaign.brandId, text);
      const categoriesResponse = categories.data;

      const transformedCategories = categoriesResponse.map((categoryInfo) => {
        const category = categoryInfo?.category || {};
        const subCategory1 = categoryInfo?.subCategory1 || {};
        const subCategory2 = categoryInfo?.subCategory2 || {};

        return {
          categoryId: category.id || "",
          categoryName: category.name || "",
          subCategory1Id: subCategory1.id || "",
          subCategory1Name: subCategory1.name || "",
          subCategory2Id: subCategory2.id || "",
          subCategory2Name: subCategory2.name || "",
          updatedAt: category.updatedAt || "",
        };
      });
      this.setState({
        productCategories: transformedCategories,
      });
    }
  };

  handleBrandCreated = (createdBrand) => {
    const { onInputChange } = this.props;

    if (!createdBrand?.id) {
      onInputChange(null, "brandId");
      onInputChange("", "brandName");
      return;
    }

    const normalizedBrand = {
      ...createdBrand,
      id: createdBrand.id,
      name: createdBrand.name || createdBrand.displayName || "",
      displayName: createdBrand.displayName || createdBrand.name || "",
      type: createdBrand.type || "parentBrand",
    };

    onInputChange(normalizedBrand, "brandId");
    onInputChange(normalizedBrand.name, "brandName");
  };

  renderCountryMenuItem = (data, { modifiers, handleClick }) => (
    <CustomMenuItem
      key={data.id}
      active={modifiers.active}
      icon={
        this.props.campaign?.countryIds?.find((c) => c?.id === data.id)
          ? IconNames.TICK
          : IconNames.BLANK
      }
      onClick={handleClick}
      text={data.name}
      shouldDismissPopover={false}
    />
  );

  render() {
    const { shouldSetCountryData, productCategories } = this.state;
    const {
      campaign,
      campaignTypes,
      onInputChange,
      hasFormSaved,
      countryList,
      searchCountries,
      campaignTargetGroups,
      removeCategory,
      actions,
    } = this.props;

    //SLATE-1399 Nov21 Anushree:- Error should be shown if required details are not filled during campaign creation when id not available
    //const showError = campaign.id && campaign.name && hasFormSaved;
    const showError = campaign.name && hasFormSaved;

    const allowModifyingStartDate = CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[
      CAMPAIGN_ACTIONS.EDIT_VALIDITY
    ].includes(campaign.status);
    //!Date Range Validation
    const today = moment().startOf("day");
    const isTgPresent = campaignTargetGroups && campaignTargetGroups.length > 0 ? true : false;
    //date range displayed if start date modification allowed and no media present
    const isDateRangeDisplayed = allowModifyingStartDate && !isTgPresent;
    //date range: {from:today, to: undefined}
    const dateRangeMin = new Date(today);
    const dateRangeMax = undefined;
    //! today validation not added in start date from, as truncating/deleting tg not handeled yet
    //start date:  {from: undefined, to: min(tg's start date)}
    const startDateMin = undefined;
    const startDateMax = moment
      .min(campaignTargetGroups?.map((tg) => moment(tg.validity.fromDate)))
      ?.toDate();
    //end date:(tg present)    { from: max(tg end date),to: undefined }
    //end date:(no tg present) { from: fixed_start_date,to: undefined }
    const endDateMin = isTgPresent
      ? moment.max(campaignTargetGroups?.map((tg) => moment(tg.validity.toDate)))?.toDate()
      : new Date(campaign.validity.fromDate);
    const endDateMax = dateRangeMax;

    const availableCategories = productCategories.filter(
      (category) =>
        !campaign?.categories?.some(
          (selectedCategory) =>
            selectedCategory?.categoryId === category?.categoryId &&
            selectedCategory?.subCategory1Id === category?.subCategory1Id &&
            selectedCategory?.subCategory2Id === category?.subCategory2Id
        )
    );

    return (
      <>
        <InfoBlock header="Campaign Details" childClassName={styles.childWrapper}>
          <div
            className={classNames(
              "flex flex-wrap clearfix",
              styles.formContainer,
              styles.basicDetails
            )}
          >
            <Form
              isFormGroup={false}
              config={[
                {
                  id: "name",
                  label: "Campaign Name",
                  type: FORM_FIELD_TYPE.INPUT,
                  className: "col-11",
                  size: {
                    lg: 6,
                    md: 6,
                  },
                  placeholder: "Enter Name",
                  value: campaign.name || "",
                  errorMessage: "Please Enter Campaign Name",
                  error: hasFormSaved && !campaign.name,
                  onChange: (e) => onInputChange(e.target.value, "name"),
                },
                {
                  id: "Campaign_Type",
                  type: FORM_FIELD_TYPE.SELECT,
                  className: "col-11",
                  size: {
                    lg: 6,
                    md: 6,
                  },
                  title: "Campaign Type",
                  list: campaignTypes?.map((item) => ({
                    ...item,
                    value: item.name,
                  })),
                  placeHolder: "Select Campaign Type",
                  showLabelInButton: false,
                  compareKey: "id",
                  textKey: "campaignTargetTypeName",
                  singleSelect: true,
                  usePortal: false,
                  onSelect: ([data]) => {
                    return onInputChange(
                      campaignTypes?.find((t) => t.id === data.id),
                      "campaignTargetTypeId"
                    );
                  },
                  selectedList: !isEmpty(campaign.campaignTargetTypeName)
                    ? [campaign.campaignTargetTypeName]
                    : [],
                  errorMessage: "Please Select Campaign Type",
                  //error: showError && isEmpty(campaign?.campaignType),
                  error: showError && isEmpty(campaign?.campaignTargetTypeName),
                },
                {
                  id: "advertiserId",
                  label: "Clients",
                  type: FORM_FIELD_TYPE.API_SEARCH,
                  showListOnClick: true,
                  className: "col-11",
                  size: {
                    lg: 6,
                    md: 6,
                  },
                  placeholder: "Search Clients",
                  value: campaign.advertiserName,
                  query: campaign.advertiserName ? campaign.advertiserName : "",
                  selectedItem: campaign.advertiserId
                    ? {
                        id: campaign.advertiserId,
                        name: campaign.advertiserName,
                        displayName: campaign.advertiserName,
                      }
                    : null,
                  errorMessage: "Please Select Clients",
                  error: showError && !campaign.advertiserId,
                  fetchAction: (text) => {
                    this.setState({ prefillClientName: text || "" });
                    return searchAdvertisers(text);
                  },
                  parseResults: (response) => {
                    const parsed =
                      response?.map((item) => ({
                        id: item.id,
                        name: item.name,
                        tag: item.cityName,
                        displayName: item.name,
                      })) || [];

                    return this.props.createOrUpdateAdvertiser
                      ? [
                          ...parsed,
                          {
                            id: "__add_new__",
                            name: "+ Add New Client",
                            displayName: "+ Add New Client",
                            isAddNew: true,
                            // style: {
                            //   color: "#1f4f82",
                            //   fontWeight: 500,
                            // },
                          },
                        ]
                      : parsed;
                  },
                  showSubText: true,
                  onSelect: (data) => {
                    if (data?.isAddNew) {
                      this.setState({
                        showAddClientPanel: true,
                        prefillClientName: this.state.prefillClientName || "",
                      });
                      return;
                    }

                    this.onAdvertiserSelect(data);
                  },
                  disabled: !CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[
                    CAMPAIGN_ACTIONS.EDIT_ADVERTISER
                  ].includes(campaign.status),
                },
                {
                  id: "brandId",
                  type: FORM_FIELD_TYPE.API_SEARCH,
                  label: "Brand",
                  placeholder: "Search for Brand",
                  className: "col-11",
                  size: {
                    lg: 6,
                    md: 6,
                  },
                  fetchAction: async (text) => {
                    const res = await searchBrandsWithAdvertiserId({
                      q: text,
                      advertiserId: campaign.advertiserId,
                    });
                    return res;
                  },
                  onSelect: (item) => {
                    if (item?.id === "__add_new__") {
                      onInputChange(null, "brandId");
                      onInputChange("", "brandName");
                      const defaultAdvertiser = campaign?.client
                        ? campaign.client
                        : campaign?.advertiser
                        ? campaign.advertiser
                        : campaign?.advertiserId
                        ? {
                            id: campaign.advertiserId,
                            name: campaign.advertiserName || campaign.advertiserId,
                          }
                        : null;

                      actions.addBrand(
                        { tabId: "parent-brands" },
                        {
                          name: (campaign.brandName ?? "").trim(),
                          advertisers: defaultAdvertiser ? [defaultAdvertiser] : [],
                        },
                        this.handleBrandCreated
                      );
                      return;
                    }
                    return onInputChange(item, "brandId");
                  },
                  query: campaign.brandName ?? "",
                  selectedItem:
                    campaign.brandId && campaign.brandName
                      ? {
                          id: campaign.brandId,
                          name: campaign.brandName,
                          displayName: campaign.brandName,
                        }
                      : null,
                  showSubText: true,
                  parseResults: (response) => {
                    const data = Array.isArray(response) ? response : response?.data || [];
                    const results = data.map((item) => ({
                      id: item[`${item.type}Id`],
                      name: this.renderBrandMenuName(item),
                      value: item[`${item.type}Name`],
                      type: item.type,
                      tag: startCase(item.type),
                    }));
                    return [
                      ...results,
                      {
                        id: "__add_new__",
                        name: "+ Add New Brand",
                        displayName: "+ Add New Brand",
                        isAddNew: true,
                      },
                    ];
                  },
                  errorMessage: "Please Add Brand",
                  error: showError && isEmpty(campaign.brandId),
                  clearSelectOnReset: true,
                  disabled: !campaign.advertiserId,
                  helperText: !campaign.advertiserId
                    ? "Select client before selecting brand"
                    : null,
                },
                {
                  id: "categoryIds",
                  type: FORM_FIELD_TYPE.MULTI_SELECT,
                  label: "Product Category",
                  className: "col-11",
                  size: {
                    lg: 6,
                    md: 6,
                  },
                  tagRenderer: this.categoryMenuRenderer,
                  tagInputProps: {
                    placeholder: "Enter Product Category",
                    tagProps: { intent: Intent.NONE, minimal: true },
                    onRemove: (data, index) => removeCategory(index),
                    disabled: !campaign.brandId,
                    rightElement:
                      campaign?.categoryIds?.length > 0 ? (
                        <RemoveIcon onClick={() => removeCategory(-1)} />
                      ) : (
                        <SearchIcon />
                      ),
                  },
                  onQueryChange: this.onBrandQueryChange,
                  onClick: () => this.onBrandQueryChange(""),
                  selectedItems: campaign?.categories ?? [],
                  items: availableCategories,
                  noResults: { message: "No Results Found" },
                  onItemSelect: (data) => onInputChange(data, "categoryIds"),
                  itemRenderer: this.renderCategoryMenuItem,
                  resetOnSelect: true,
                  errorMessage: "Please Enter Product Category",
                  error: showError && isEmpty(campaign.categoryIds),
                  helperText: !campaign.brandId
                    ? "Select client and brand before selecting product category"
                    : null,
                },
                {
                  type: FORM_FIELD_TYPE.DATE_RANGE_PICKER,
                  formatDate: (date) => moment(date).format("DD/MM/YYYY"),
                  title: "Validity",
                  containerClassName: "col-11",
                  size: {
                    lg: 4,
                    md: 4,
                  },
                  errorMessage: "Please Select Validity",
                  error: showError && isEmpty(campaign?.validity),
                  handleDateChange: (dateRange) => onInputChange(dateRange, "validity"),
                  dateRange: getDateRange(campaign?.validity),
                  isLabel: !CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[
                    CAMPAIGN_ACTIONS.EDIT_VALIDITY
                  ].includes(campaign.status),
                  //isHidden: !allowModifyingStartDate,
                  isHidden: !isDateRangeDisplayed,
                  minDate: dateRangeMin,
                  maxDate: dateRangeMax,
                  initialMonth: dateRangeMin,
                },
                {
                  id: "startDate",
                  //type: FORM_FIELD_TYPE.INPUT,
                  type: allowModifyingStartDate ? FORM_FIELD_TYPE.DATE : FORM_FIELD_TYPE.INPUT,
                  label: "Validity (Start Date)",
                  title: "Validity (Start Date)",
                  size: { lg: 2, md: 2 },
                  onChange: (v) => onInputChange([v, null], "validity"),
                  value: !allowModifyingStartDate
                    ? moment(campaign.validity.fromDate).format("MMM DD, YYYY")
                    : new Date(campaign.validity.fromDate),
                  placeHolder: "Start Date",
                  disabled: !allowModifyingStartDate,
                  isHidden: isDateRangeDisplayed,
                  minDate: startDateMin,
                  maxDate: startDateMax,
                  initialMonth: startDateMax,
                  defaultValue: startDateMax,
                },
                {
                  id: "endDate",
                  type: FORM_FIELD_TYPE.DATE,
                  title: "Validity (End Date)",
                  size: { lg: 2, md: 2 },
                  onChange: (v) => onInputChange([null, v], "validity"),
                  value: new Date(campaign.validity.toDate),
                  placeholder: "End Date",
                  //isHidden: allowModifyingStartDate,
                  isHidden: isDateRangeDisplayed,
                  minDate: endDateMin,
                  maxDate: endDateMax,
                  initialMonth: endDateMin,
                  disabled: endDateMax < endDateMin,
                  defaultValue: endDateMin,
                },
                {
                  type: FORM_FIELD_TYPE.LABEL,
                  title: "Total Duration",
                  className: "col-11",
                  size: {
                    lg: 2,
                    md: 2,
                  },
                  value:
                    !isEmpty(campaign?.validity) &&
                    campaign.validity.fromDate &&
                    campaign.validity.toDate
                      ? ` ${
                          moment(campaign.validity.toDate).diff(
                            moment(campaign.validity.fromDate),
                            "days"
                          ) + 1
                        } days`
                      : "0 days",
                },

                // {
                //   id: "maxPlaysPerScreen",
                //   title: "Max Plays Per Screen",
                //   type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                //   size: {
                //     lg: 6,
                //     md: 6,
                //   },
                //   placeholder: "Enter Plays Per Screen",
                //   value: campaign?.maxPlaysPerScreen ? campaign?.maxPlaysPerScreen : 0,
                //   // errorMessage: "Please Enter Max Plays Per Screen",
                //   // error: showError && !campaign?.maxPlaysPerScreen,
                //   onValueChange: (value) => onInputChange(value, "maxPlaysPerScreen"),
                // },
                // {
                //   id: "budgetedPlays",
                //   title: "Budgeted Plays",
                //   type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                //   size: {
                //     lg: 6,
                //     md: 6,
                //   },
                //   placeholder: "Enter Budgeted Plays",
                //   value: campaign?.budgetedPlays || 0,
                //   onValueChange: (value) => onInputChange(value, "budgetedPlays"),
                // },
                // {
                //   id: "maxPlaysPerScreen",
                //   title: "Max Plays Per Screen",
                //   type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                //   size: {
                //     lg: 6,
                //     md: 6,
                //   },
                //   placeholder: "Enter Plays Per Screen",
                //   value: campaign?.maxPlaysPerScreen ? campaign?.maxPlaysPerScreen : 0,
                //   // errorMessage: "Please Enter Max Plays Per Screen",
                //   // error: showError && !campaign?.maxPlaysPerScreen,
                //   onValueChange: (value) => onInputChange(value, "maxPlaysPerScreen"),
                // },
                // {
                //   id: "budgetedPlays",
                //   title: "Budgeted Plays",
                //   type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                //   size: {
                //     lg: 6,
                //     md: 6,
                //   },
                //   placeholder: "Enter Budgeted Plays",
                //   value: campaign?.budgetedPlays || 0,
                //   onValueChange: (value) => onInputChange(value, "budgetedPlays"),
                // },
                // {
                //   id: "maxPlaysPerScreen",
                //   title: "Max Plays Per Screen",
                //   type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                //   size: {
                //     lg: 6,
                //     md: 6,
                //   },
                //   placeholder: "Enter Plays Per Screen",
                //   value: campaign?.maxPlaysPerScreen ? campaign?.maxPlaysPerScreen : 0,
                //   // errorMessage: "Please Enter Max Plays Per Screen",
                //   // error: showError && !campaign?.maxPlaysPerScreen,
                //   onValueChange: (value) => onInputChange(value, "maxPlaysPerScreen"),
                // },
                // {
                //   id: "budgetedPlays",
                //   title: "Budgeted Plays",
                //   type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                //   size: {
                //     lg: 6,
                //     md: 6,
                //   },
                //   placeholder: "Enter Budgeted Plays",
                //   value: campaign?.budgetedPlays || 0,
                //   onValueChange: (value) => onInputChange(value, "budgetedPlays"),
                // },
                {
                  id: "currency",
                  label: "Currency",
                  title: "Currency",
                  type: FORM_FIELD_TYPE.API_SEARCH,
                  size: {
                    lg: 6,
                    md: 6,
                    sm: 6,
                  },
                  className: "col col-11",
                  placeholder: "Search currency",
                  onSelect: (data) => onInputChange(data, "currencyId"),
                  query: campaign?.currencyName ?? "",
                  searchIcon: "right",
                  value: campaign.currencyName,
                  showSubText: false,
                  parseResults: (data) => {
                    return data?.map((item) => {
                      return {
                        id: item.id,
                        name: item.name,
                        tag: item.type,
                        item: item,
                      };
                    });
                  },
                  errorMessage: "Please Select Currency",
                  error: showError && isEmpty(campaign?.currencyId),
                  fetchAction: (text) => searchCurrencies(text),
                  disabled: !CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[
                    CAMPAIGN_ACTIONS.EDIT_CURRENCY
                  ].includes(campaign.status),
                },
                {
                  id: "countries", // TODO: Need to remove countries and move it to location targeting
                  label: "Country",
                  type: FORM_FIELD_TYPE.MULTI_SELECT,
                  size: {
                    lg: 6,
                    md: 6,
                    sm: 6,
                  },
                  className: `col col-11 ${styles.countrySelector}`,
                  //isHidden: isEmpty(campaign?.currencyId),
                  items: countryList ?? [],
                  onItemSelect: (data) => onInputChange(data, "countryIds"),
                  itemRenderer: this.renderCountryMenuItem,
                  resetOnSelect: true,
                  tagRenderer: (data) => data.name,
                  tagInputProps: {
                    placeholder: "Search Countries",
                    tagProps: { intent: Intent.NONE, minimal: true },
                    onRemove: (data, index) => this.onCountryRemove(index),
                    rightElement:
                      campaign?.countryIds?.length > 0 ? (
                        <RemoveIcon onClick={() => this.onCountryRemove("clear")} />
                      ) : (
                        <SearchIcon />
                      ),
                    disabled: !CAMPAIGN_ACTIONS_ALLOWED_STATUS_LIST[
                      CAMPAIGN_ACTIONS.EDIT_COUNTRY
                    ].includes(campaign.status),
                  },
                  onQueryChange: (text) => searchCountries({ q: text }),
                  selectedItems: campaign?.countries ?? [],
                  value: campaign?.countries?.map((c) => c.name).join(", ") ?? "",
                  errorMessage: "Please Select Country",
                  error: showError && isEmpty(campaign?.countryIds),
                },
                // {
                //   id: "effectiveRate",
                //   title: "Effective Rate",
                //   type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                //   size: {
                //     lg: 3,
                //     md: 3,
                //   },
                //   placeholder: "Enter Effective Rate",
                //   value: campaign?.effectiveRate,
                //   onValueChange: (value) => onInputChange(value, "effectiveRate"),
                //   isOptional: true,
                // },
                {
                  id: "orderId",
                  label: "Order ID",
                  type: FORM_FIELD_TYPE.INPUT,
                  className: "col col-11",
                  size: {
                    lg: 6,
                    md: 6,
                    sm: 6,
                  },
                  placeholder: "Enter Order ID",
                  value: campaign?.orderId || "",
                  onChange: (e) =>
                    onInputChange(e.target.value.replace(/\s+/g, "").trim(), "orderId"),
                  errorMessage: "Order ID is required for government campaigns.",
                  error:
                    hasFormSaved &&
                    !campaign.orderId &&
                    campaign.campaignTargetTypeName.toLowerCase().includes("government"),
                  isOptional: !campaign.campaignTargetTypeName.toLowerCase().includes("government"),
                },
                {
                  id: "thirdPartyCampaignId",
                  label: "Third-Party Campaign ID",
                  type: FORM_FIELD_TYPE.INPUT,
                  className: "col col-11",
                  size: {
                    lg: 6,
                    md: 6,
                    sm: 6,
                  },
                  placeholder: "Enter Third Party Campaign ID",
                  value: campaign?.thirdPartyCampaignId || "",
                  onChange: (e) =>
                    onInputChange(
                      e.target.value.replace(/\s+/g, "").trim(),
                      "thirdPartyCampaignId"
                    ),
                  errorMessage: "Third-party Campaign ID is required.",
                  error: hasFormSaved && !campaign.thirdPartyCampaignId,
                },
                {
                  id: "campaignValue",
                  title: "Campaign Value",
                  type: FORM_FIELD_TYPE.NUMERIC_INPUT,
                  size: {
                    lg: 3,
                    md: 3,
                  },
                  placeholder: "Enter Campaign Value",
                  value: campaign?.campaignValue,
                  onValueChange: (value) => onInputChange(value, "campaignValue"),
                  errorMessage: "Please Enter Campaign Value",
                  error: showError && !campaign?.campaignValue,
                },
              ]}
            />
            {this.props.createOrUpdateAdvertiser && (
              <EditAdvertiser
                isOpen={this.state.showAddClientPanel}
                advertiser={{ name: this.state.prefillClientName }}
                onUpdate={this.handleClientCreate}
                cancelEdit={() => this.setState({ showAddClientPanel: false })}
              />
            )}
          </div>
        </InfoBlock>
        <Popup
          isOpen={!!shouldSetCountryData || shouldSetCountryData === 0}
          onClose={() => this.setState({ shouldSetCountryData: null })}
          onConfirm={() => {
            this.onCountryRemove(shouldSetCountryData, true);
          }}
          title="Popup.title"
          body="Popup.campaignCurrencyChangeConfirmation"
        />
      </>
    );
  }
}
